import { Dropdown as AntDropdown } from 'antd';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { NftMedia } from 'components/Shared/NftMedia';
import { DEVICE } from 'constants/app/breakpoints';
import { IS_KIOSK_VENICE_MODE } from 'constants/app/configs';
import { TSizeType } from 'interfaces';

export const SIZE_STYLES: Record<
  TSizeType,
  Record<'wrapper' | 'media' | 'name' | 'price', FlattenSimpleInterpolation>
> = {
  huge: {
    wrapper: css`
      width: 383px;
      border-radius: 24px;
      padding: 16px;
      grid-template-rows: auto 16px auto 12px auto 8px auto;
    `,
    media: css`
      border-radius: 24px;
    `,
    name: css`
      font-size: 16px;
      line-height: 24px;
    `,
    price: css`
      font-size: 16px;
    `
  },
  large: {
    wrapper: css`
      width: 383px;
      border-radius: 24px;
      padding: 16px;
      grid-template-rows: auto 16px auto 12px auto 8px auto;
    `,
    media: css`
      border-radius: 24px;
    `,
    name: css`
      font-size: 16px;
      line-height: 24px;
    `,
    price: css`
      font-size: 16px;
    `
  },
  medium: {
    wrapper: css`
      width: 280px;
      border-radius: 20px;
      padding: 16px;
      grid-template-rows: auto 9px auto 12px auto 8px auto;
    `,
    media: css`
      border-radius: 20px;
    `,
    name: css`
      font-size: 15px;
      line-height: 20px;
    `,
    price: css`
      font-size: 14px;
    `
  },
  small: {
    wrapper: css`
      width: 171px;
      border-radius: 16px;
      padding: 12px;
      grid-template-rows: auto 8px auto 8px auto 2px auto;
    `,
    media: css`
      border-radius: 16px;
    `,
    name: css`
      font-size: 14px;
      line-height: 20px;
    `,
    price: css`
      font-size: 12px;
    `
  }
};

export const NftCardMedia = styled(NftMedia)<{ $isDeleted?: boolean }>`
  grid-column-start: 1;
  grid-column-end: 13;
  ${props =>
    props.$isDeleted &&
    css`
      img {
        opacity: 0.38;
      }
    `}
`;

export const NftCardMediaSkeleton = styled.div`
  grid-column-start: 1;
  grid-column-end: 13;
  overflow: hidden;
`;

export const NftCardAvatarTextWrapper = styled.div`
  grid-column-start: 1;
  grid-column-end: 13;
  grid-row-start: 3;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  && {
    li.ant-dropdown-menu-item {
      padding: unset;
    }
  }
`;

export const NftCardMenuToggle = styled.div<{ $isDeleted?: boolean }>`
  padding: 7px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--v3-black-0);
  border-radius: 8px;
  cursor: ${props => (props.$isDeleted ? 'default' : 'pointer')};

  &:hover {
    background: var(--v3-black-1);
  }
`;

export const NftCardName = styled.div<{ $isDeleted?: boolean }>`
  color: var(--v3-text-color);
  font-weight: 500;
  grid-column-start: 1;
  grid-column-end: 13;
  grid-row-start: 5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  ${props =>
    props.$isDeleted &&
    css`
      opacity: 0.38;
    `}
`;

export const NftDeletedText = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  background: var(
    --gradientz,
    linear-gradient(96deg, #ff4387 0%, #4743ff 104.58%)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  grid-column-start: 1;
  grid-column-end: 13;
  grid-row-start: 7;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  @media only screen and ${DEVICE.sm} {
    font-size: 11px;
  }
`;

export const NftCardPriceWrapper = styled.div<{
  $isContentEmpty?: boolean;
}>`
  grid-column-start: 1;
  grid-column-end: 13;
  grid-row-start: 7;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  color: var(--v3-text-color);
  font-weight: 400;
  line-height: 24px;

  & > span {
    font-weight: 700;
    color: var(--v3-primary-color);
  }

  ${props =>
    props.$isContentEmpty &&
    css`
      & > span {
        color: transparent;
        user-select: none;
      }
    `}
`;

export const NftCardWrapper = styled.div<{
  $isDeleted: boolean;
  $size: TSizeType;
  $flexible: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(12, auto);
  align-items: center;
  border: 1px solid var(--v3-black-2);
  height: fit-content;

  ${!IS_KIOSK_VENICE_MODE &&
  css`
    cursor: ${props => (props.$isDeleted ? 'default' : 'pointer')};
  `}
  ${props => SIZE_STYLES[props.$size].wrapper}
  ${props =>
    props.$flexible &&
    css`
      width: 100%;
    `}

  ${NftCardMedia}, ${NftCardMediaSkeleton} {
    ${props => SIZE_STYLES[props.$size].media}
  }

  ${NftCardName} {
    ${props => SIZE_STYLES[props.$size].name}
  }

  ${NftCardPriceWrapper} {
    ${props => SIZE_STYLES[props.$size].price}
  }

  && .deleted {
    opacity: 0.38;
  }
`;

export const Dropdown = styled(AntDropdown)`
  * {
    word-break: unset !important;
  }
`;
